<template>
  <text-field
    ref="textFieldRef"
    v-model="purePhone"
    v-bind="$attrs"
    :disabled="disabled"
    :class="disabled ? 'phone-select-disabled' : 'phone-select'"
    :rules="[{ required: !!required, validator: validatePhone, trigger: 'blur' }]"
    >
    <template #prepend-input>
      <div style="width: 52px;">
        <el-select
          v-model="countryCodeOption"
          value-key="regionName"
          filterable
          :disabled="disabled"
          @change="onCountryCodeChange"
          >
          <div slot="prefix" v-if="!!countryCodeOption">
            <div style="margin-top: 10px;">
              <span :class="countryCodeOption.flagClass" style="margin-right: 4px;"></span>
            </div>
          </div>
          <el-option v-for="opt in regionOptions" :key="opt.code" :label="opt.label" :value="opt">
            <div style="margin-left: 20px;">
              <span :class="opt.flagClass" style="margin-right: 4px;"></span>
              <span>{{ opt.regionName }}</span>
              <span>{{ opt.label }}</span>
            </div>
          </el-option>
        </el-select>
      </div>
    </template>
  </text-field>
</template>

<script>
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { debounce as _debounce } from 'lodash'
import { phoneHelpers } from '@/utils/phone'
import { getTransText } from '@/utils/i18n'

export default {
  name: 'PhoneField',
  props: {
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /** 'mobile' || 'phone' */
    phoneType: {
      type: String,
      default: 'mobile'
    },
    errorKey: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    defaultCountryCode: {
      type: String,
      default: 'TW'
    }
  },
  setup (props, ctx) {
    const vm = getCurrentInstance()
    const { emit } = ctx
    const nationalPhone = ref('')
    const purePhone = ref('')
    const allRegions = ref(phoneHelpers.getSupportedRegions())
    const countryCodeOption = ref({})
    const textFieldRef = ref(null)

    const regionOptions = computed(() => allRegions.value.map((code, index) => {
      const regionName = getRegionName(code)
      const callingCode = phoneHelpers.getCountryCodeForRegion(code)
      return {
        label: `+${callingCode}`,
        regionName,
        callingCode,
        value: code,
        code,
        flagClass: `fi fi-${code.toLowerCase()}`
      }
    }))

    const regionNames = computed(() => new Intl.DisplayNames([vm.proxy.$i18n.locale], { type: 'region' }))

    const validPhoneFunc = computed(() => {
      if (props.phoneType === 'mobile') {
        return phoneHelpers.isValidMobileNumber
      }
      return phoneHelpers.isValidNumber
    })

    watch(
      [
        () => props.value,
        () => props.defaultCountryCode
      ],
      ([val, newCountryCode]) => {
        nationalPhone.value = val
        if (!val) {
          countryCodeOption.value = regionOptions.value.find(opt => opt.code === newCountryCode)
          purePhone.value = ''
          return
        }
        updatePurePhone(val)
      }, { immediate: true }
    )

    watch(
      purePhone,
      _debounce((val) => {
        updateNationalPhone(purePhone.value, countryCodeOption.value)
      }, 100)
    )

    // handle locale change, update country code to match the region data
    watch(() => vm.proxy.$i18n.locale, () => {
      countryCodeOption.value = regionOptions.value.find(opt => opt.code === countryCodeOption.value.code) || {}
    })

    function getRegionName (countryCode = 'TW') {
      try {
        return regionNames.value.of(countryCode) || ''
      } catch (error) {
        console.error(error)
        return ''
      }
    }

    function onCountryCodeChange (countryCodeOption) {
      updateNationalPhone(purePhone.value, countryCodeOption)
      window.setTimeout(() => {
        textFieldRef.value.$el.querySelectorAll('input')[1].focus()
      }, 10)
    }

    function updateNationalPhone (purePhone, countryCodeOption) {
      const isValidNumber = validPhoneFunc.value(purePhone, countryCodeOption.code)
      if (!isValidNumber) {
        nationalPhone.value = purePhone !== '' ? `${countryCodeOption.label} ${purePhone}` : ''
      } else {
        nationalPhone.value = phoneHelpers.toInternationalFormat(purePhone, countryCodeOption.code)
      }
      emit('input', nationalPhone.value)
    }

    function validatePhone (rule, value, callback) {
      if (!props.required && !purePhone.value) {
        callback()
        return
      }
      const isValidNumber = validPhoneFunc.value(purePhone.value, countryCodeOption.value.code)
      if (!isValidNumber) {
        const errorMessage = props.errorKey ? vm.proxy.$i18n.t(props.errorKey) : getTransText.call(vm.proxy, ctx.attrs.name, 'error', ctx.attrs.base)
        callback(new Error(errorMessage))
      } else {
        callback()
      }
    }

    function updatePurePhone (val = '') {
      const [_countryCodeLabel, _purePhone] = val.split(' ')
      countryCodeOption.value = regionOptions.value.find(opt => opt.label === _countryCodeLabel) || ''
      purePhone.value = _purePhone || ''
    }

    return {
      textFieldRef,
      nationalPhone,
      purePhone,
      countryCodeOption,
      regionOptions,
      onCountryCodeChange,
      validatePhone: _debounce(validatePhone, 100)
    }
  }
}
</script>

<style lang="scss">
.phone-select-disabled {
  .is-disabled .el-input__inner {
    color: #909399 !important;
  }
}
.phone-select {
  .el-input__inner {
    color: #6f6f6f !important;
  }
  .el-input-group__append, .el-input-group__prepend {
    background-color: #fff;
  }
}
</style>
